import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { sharePizarra } from "@/lib/sharePizarra";
import { toast } from "sonner";
import { Play, Pause, Flag, X, RotateCcw, Save } from "lucide-react";

const LS_KEY = "sude_pizarra_v1";
const LS_TS = "sude_pizarra_ts_v1";
const LS_SYNCED = "sude_pizarra_synced_v1";

const FORMATIONS = {
  F7: {
    "2-3-1": [[50, 90], [32, 72], [68, 72], [20, 48], [50, 48], [80, 48], [50, 25]],
    "3-2-1": [[50, 90], [22, 72], [50, 72], [78, 72], [35, 46], [65, 46], [50, 24]],
    "2-2-2": [[50, 90], [32, 72], [68, 72], [32, 48], [68, 48], [32, 25], [68, 25]],
  },
  F8: {
    "3-3-1": [[50, 90], [22, 72], [50, 72], [78, 72], [22, 48], [50, 48], [78, 48], [50, 25]],
    "2-3-2": [[50, 90], [35, 74], [65, 74], [20, 50], [50, 50], [80, 50], [35, 27], [65, 27]],
    "3-2-2": [[50, 90], [22, 72], [50, 72], [78, 72], [35, 48], [65, 48], [35, 26], [65, 26]],
    "2-4-1": [[50, 90], [35, 74], [65, 74], [15, 50], [38, 50], [62, 50], [85, 50], [50, 25]],
  },
  F11: {
    "4-3-3": [[50, 92], [15, 78], [38, 78], [62, 78], [85, 78], [25, 55], [50, 55], [75, 55], [20, 30], [50, 28], [80, 30]],
    "4-4-2": [[50, 92], [15, 78], [38, 78], [62, 78], [85, 78], [15, 52], [38, 52], [62, 52], [85, 52], [38, 28], [62, 28]],
    "4-2-3-1": [[50, 92], [15, 78], [38, 78], [62, 78], [85, 78], [38, 62], [62, 62], [20, 45], [50, 45], [80, 45], [50, 25]],
    "3-4-3": [[50, 92], [25, 78], [50, 78], [75, 78], [15, 55], [38, 55], [62, 55], [85, 55], [20, 30], [50, 28], [80, 30]],
  },
};
const MAX_FIELD = { F7: 7, F8: 8, F11: 11 };

export const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const photoSrc = (p) => (p?.photo_url ? `${BACKEND_URL}${p.photo_url}` : null);

// Ficha visual de jugador: foto (si existe) + dorsal + nombre + insignias de acciones.
export function PlayerToken({ p, size = 52, color = "#1d4ed8", selected = false, badges = [], showName = true }) {
  const src = photoSrc(p);
  return (
    <div className="flex flex-col items-center pointer-events-none select-none">
      <div className="relative">
        <div className={`relative rounded-full overflow-hidden flex items-center justify-center font-black text-white shadow-lg ${selected ? "ring-2 ring-sky-400 animate-pulse" : ""}`}
          style={{ width: size, height: size, background: color, border: "2px solid white", fontSize: size * 0.38 }}>
          <span>{p?.jersey_number || "?"}</span>
          {src && <img src={src} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" />}
        </div>
        {src && (
          <span className="absolute -bottom-1 -right-1 rounded-full flex items-center justify-center font-black text-white leading-none"
            style={{ width: Math.max(18, size * 0.4), height: Math.max(18, size * 0.4), background: color, border: "1.5px solid white", fontSize: Math.max(9, size * 0.2) }}>
            {p?.jersey_number || "?"}
          </span>
        )}
        {badges.length > 0 && (
          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 flex gap-0.5 whitespace-nowrap bg-black/75 rounded px-1 py-0.5 leading-none">
            {badges.map((b, i) => <span key={i} className="text-[11px] leading-none">{b}</span>)}
          </div>
        )}
      </div>
      {showName && <div className="mt-0.5 px-1 bg-black/70 text-white text-[9px] font-heading uppercase truncate rounded-sm" style={{ maxWidth: size + 22 }}>{(p?.name || "?").split(" ")[0]}</div>}
    </div>
  );
}

// Avatar pequeño para listas (foto o dorsal).
export function MiniAvatar({ p, className = "" }) {
  const src = photoSrc(p);
  return (
    <span className={`w-8 h-8 rounded-full overflow-hidden flex items-center justify-center text-xs font-black flex-shrink-0 bg-muted text-foreground ${className}`}>
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : (p?.jersey_number || "?")}
    </span>
  );
}

export default function CoachPizarra() {
  const navigate = useNavigate();
  const [state, setState] = useState(() => {
    try { const s = localStorage.getItem(LS_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [remoteReady, setRemoteReady] = useState(false);
  const syncTimer = useRef(null);
  const applyingRemote = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, p] = await Promise.all([api.get("/teams"), api.get("/players")]);
        setTeams(t.data); setPlayers(p.data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  // Recupera la sesión del servidor: permite seguir el partido en otro dispositivo.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/pizarra/session");
        const remote = r.data?.state;
        const remoteTs = r.data?.updated_at || 0;
        const localTs = Number(localStorage.getItem(LS_TS) || 0);
        if (remote && remoteTs > localTs) {
          applyingRemote.current = true;
          localStorage.setItem(LS_TS, String(remoteTs));
          localStorage.setItem(LS_SYNCED, "1");
          setState(remote);
        } else if (!remote && localStorage.getItem(LS_SYNCED) === "1") {
          // El partido se cerró/descartó en otro dispositivo
          applyingRemote.current = true;
          localStorage.removeItem(LS_SYNCED);
          localStorage.removeItem(LS_TS);
          setState(null);
        }
      } catch { /* sin conexión: seguimos con lo local */ }
      setRemoteReady(true);
    })();
  }, []);

  // Polling: refleja al momento lo que se haga en otro dispositivo (misma cuenta).
  useEffect(() => {
    if (!remoteReady) return;
    const iv = setInterval(async () => {
      try {
        const r = await api.get("/pizarra/session");
        const remote = r.data?.state;
        const remoteTs = r.data?.updated_at || 0;
        const localTs = Number(localStorage.getItem(LS_TS) || 0);
        if (remote && remoteTs > localTs) {
          applyingRemote.current = true;
          localStorage.setItem(LS_TS, String(remoteTs));
          localStorage.setItem(LS_SYNCED, "1");
          setState(remote);
        } else if (!remote && localStorage.getItem(LS_SYNCED) === "1") {
          applyingRemote.current = true;
          localStorage.removeItem(LS_SYNCED);
          localStorage.removeItem(LS_TS);
          setState(null);
        }
      } catch { /* sin conexión */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [remoteReady]);

  useEffect(() => {
    if (state) localStorage.setItem(LS_KEY, JSON.stringify(state));
    else localStorage.removeItem(LS_KEY);
    if (!remoteReady) return;
    if (applyingRemote.current) { applyingRemote.current = false; return; }
    const ts = Date.now();
    localStorage.setItem(LS_TS, String(ts));
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      if (state) {
        api.put("/pizarra/session", { state, updated_at: ts })
          .then(() => localStorage.setItem(LS_SYNCED, "1"))
          .catch(() => {});
      } else {
        api.delete("/pizarra/session")
          .then(() => { localStorage.removeItem(LS_SYNCED); localStorage.removeItem(LS_TS); })
          .catch(() => {});
      }
    }, 1200);
    return () => clearTimeout(syncTimer.current);
  }, [state, remoteReady]);

  useEffect(() => {
    const st = document.createElement("style");
    st.innerHTML = "chat-widget{display:none !important}";
    document.head.appendChild(st);
    return () => st.remove();
  }, []);

  if (!state || state.phase === "setup" || !state.phase) {
    return <Setup teams={teams} players={players} initial={state} onStart={(s) => setState(s)} />;
  }
  if (state.phase === "board") {
    return <Board state={state} setState={setState} players={players} teamName={teams.find((t) => t.id === state.teamId)?.name || "Mi equipo"} onFinish={() => setState((s) => finishState(s))} />;
  }
  return <Summary state={state} setState={setState} players={players} navigate={navigate} />;
}

function finishState(s) {
  const sec = elapsedOf(s.clock);
  const segments = { ...s.segments };
  Object.keys(segments).forEach((pid) => {
    segments[pid] = segments[pid].map((seg) => (seg.out == null ? { ...seg, out: sec } : seg));
  });
  return { ...s, phase: "summary", finalSec: sec, segments, clock: { ...s.clock, running: false, accSec: sec } };
}

export const elapsedOf = (c) => (c.running ? Math.floor(c.accSec + (Date.now() - c.lastStart) / 1000) : c.accSec);

/* ---------------- SETUP ---------------- */
function Setup({ teams, players, initial, onStart }) {
  const [teamId, setTeamId] = useState("");
  const [opponent, setOpponent] = useState("");
  const [homeAway, setHomeAway] = useState("casa");
  const [dateStr, setDateStr] = useState(() => new Date().toISOString().slice(0, 10));
  const [modality, setModality] = useState("F11");
  const [formation, setFormation] = useState("4-3-3");
  const [starters, setStarters] = useState([]);
  const [matches, setMatches] = useState([]);
  const [matchId, setMatchId] = useState("");

  useEffect(() => { if (!teamId && teams[0]) setTeamId(teams[0].id); }, [teams]);
  useEffect(() => { setFormation(Object.keys(FORMATIONS[modality])[0]); setStarters((st) => st.slice(0, MAX_FIELD[modality])); }, [modality]);
  useEffect(() => { setStarters([]); setMatchId(""); }, [teamId]);

  useEffect(() => {
    if (!teamId) return;
    const today = new Date();
    const from = new Date(today.getTime() - 2 * 864e5).toISOString().slice(0, 10);
    const to = new Date(today.getTime() + 30 * 864e5).toISOString().slice(0, 10);
    api.get(`/calendar?date_from=${from}&date_to=${to}&team_id=${teamId}`)
      .then((r) => setMatches((r.data?.events || []).filter((e) => e.type === "partido" && !e.virtual)))
      .catch(() => setMatches([]));
  }, [teamId]);

  const selectedMatch = useMemo(() => matches.find((m) => m.id === matchId) || null, [matches, matchId]);
  const convocadosIds = selectedMatch?.convocados?.length ? selectedMatch.convocados : null;

  const pickMatch = (id) => {
    setMatchId(id);
    setStarters([]);
    const m = matches.find((x) => x.id === id);
    if (m) {
      setOpponent(m.opponent || "");
      setDateStr(m.date);
      if (m.home_away) setHomeAway(m.home_away);
    }
  };

  const teamPlayers = useMemo(() => {
    const base = players.filter((p) => p.team_id === teamId);
    return convocadosIds ? base.filter((p) => convocadosIds.includes(p.id)) : base;
  }, [players, teamId, convocadosIds]);
  const maxN = MAX_FIELD[modality];

  const toggle = (pid) => setStarters((st) => st.includes(pid)
    ? st.filter((x) => x !== pid)
    : (st.length >= maxN ? (toast.error(`Máximo ${maxN} titulares en ${modality}`), st) : [...st, pid]));

  const start = () => {
    if (!teamId || starters.length === 0) { toast.error("Selecciona al menos 1 titular"); return; }
    if (starters.length < maxN && !window.confirm(
      `Vas a empezar con ${starters.length} de ${maxN} jugadores en el campo (${modality}).\n` +
      `Solo debería ocurrir por falta de convocados, lesiones o decisión del equipo.\n¿Continuar?`)) return;
    const pos = FORMATIONS[modality][formation];
    const onField = starters.map((pid, i) => ({ player_id: pid, x: pos[i]?.[0] ?? 50, y: pos[i]?.[1] ?? 50 }));
    const bench = teamPlayers.filter((p) => !starters.includes(p.id)).map((p) => p.id);
    const segments = {};
    starters.forEach((pid) => { segments[pid] = [{ in: 0, out: null }]; });
    onStart({
      phase: "board", teamId, opponent: opponent || "Rival", homeAway, date: dateStr, modality, formation,
      starters, gkId: starters[0], convocados: [...starters, ...bench], onField, bench, segments, events: [],
      clock: { running: false, accSec: 0, lastStart: Date.now() },
    });
  };

  return (
    <div className="max-w-2xl pb-28">
      <div className="overline">Pizarra táctil · Partido en directo</div>
      <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2 mb-2">Pizarra</h1>
      <p className="text-muted-foreground text-sm mb-6">Coloca a los titulares en el campo y registra los cambios arrastrando o tocando — sin límite de sustituciones.</p>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-1">Equipo</label>
            <select data-testid="pz-team" value={teamId} onChange={(e) => setTeamId(e.target.value)} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm">
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Fecha</label>
            <input data-testid="pz-date" type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm" />
          </div>
        </div>
        {matches.length > 0 && (
          <div>
            <label className="overline block mb-1">Partido programado (del calendario)</label>
            <select data-testid="pz-match" value={matchId} onChange={(e) => pickMatch(e.target.value)} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm">
              <option value="">— Partido manual (sin vincular) —</option>
              {matches.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.date}{m.time ? ` ${m.time}` : ""} · vs {m.opponent || "Rival"}{m.convocados?.length ? ` · ${m.convocados.length} convocados` : ""}
                </option>
              ))}
            </select>
            {convocadosIds && (
              <p className="text-[11px] text-emerald-400 mt-1" data-testid="pz-convocados-info">
                ✓ Solo se muestran los {convocadosIds.length} jugadores convocados a este partido
              </p>
            )}
            {selectedMatch && !convocadosIds && (
              <p className="text-[11px] text-amber-400 mt-1">Este partido no tiene convocatoria — se muestra toda la plantilla</p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-1">Rival</label>
            <input data-testid="pz-opponent" type="text" value={opponent} onChange={(e) => setOpponent(e.target.value)} placeholder="CD Rival" className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm" />
          </div>
          <div>
            <label className="overline block mb-1">Local / Visitante</label>
            <select data-testid="pz-homeaway" value={homeAway} onChange={(e) => setHomeAway(e.target.value)} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm">
              <option value="casa">En casa</option>
              <option value="fuera">Fuera</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-1">Modalidad</label>
            <div className="grid grid-cols-3 gap-1">
              {["F7", "F8", "F11"].map((m) => (
                <button key={m} data-testid={`pz-mod-${m}`} onClick={() => setModality(m)} className={`h-10 border-2 font-heading font-black text-xs ${modality === m ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>{m}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="overline block mb-1">Formación inicial</label>
            <select data-testid="pz-formation" value={formation} onChange={(e) => setFormation(e.target.value)} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm">
              {Object.keys(FORMATIONS[modality]).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="overline block mb-2">Titulares ({starters.length}/{MAX_FIELD[modality]}) — el resto van al banquillo</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5" data-testid="pz-starters">
            {teamPlayers.map((p) => {
              const sel = starters.includes(p.id);
              return (
                <button key={p.id} data-testid={`pz-starter-${p.id}`} onClick={() => toggle(p.id)} className={`h-12 px-2 border-2 flex items-center gap-2 text-left ${sel ? "border-primary bg-primary/10" : "border-border"}`}>
                  <MiniAvatar p={p} className={sel ? "ring-2 ring-primary" : ""} />
                  <span className="font-heading font-bold text-[11px] uppercase truncate flex-1">{p.name}</span>
                  <span className={`text-[10px] font-black ${sel ? "text-primary" : "text-muted-foreground"}`}>#{p.jersey_number || "?"}</span>
                </button>
              );
            })}
            {teamPlayers.length === 0 && <div className="col-span-full text-sm text-muted-foreground p-4">Este equipo no tiene jugadores.</div>}
          </div>
        </div>
        <button data-testid="pz-start" onClick={start} className="btn-volt w-full h-12 justify-center text-sm"><Play size={16} /> Empezar partido</button>
      </div>
    </div>
  );
}

/* ---------------- BOARD ---------------- */
function Board({ state, setState, players, teamName, onFinish }) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!state.clock.running) return;
    const iv = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(iv);
  }, [state.clock.running]);

  const fieldRef = useRef(null);
  const [selectedBench, setSelectedBench] = useState(null);
  const [actionFor, setActionFor] = useState(null);
  const [view, setView] = useState("pizarra");
  const [sharing, setSharing] = useState(false);
  const [showHelp, setShowHelp] = useState(() => !localStorage.getItem("sude_pizarra_help_v1"));
  const drag = useRef(null);

  const closeHelp = () => { localStorage.setItem("sude_pizarra_help_v1", "1"); setShowHelp(false); };
  const setCounter = (key, delta) => setState((s) => ({
    ...s, teamCounters: { ...(s.teamCounters || {}), [key]: Math.max(0, ((s.teamCounters || {})[key] || 0) + delta) },
  }));

  const byId = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);
  const elapsed = elapsedOf(state.clock);
  const minute = Math.floor(elapsed / 60) + 1;
  const gf = state.events.filter((e) => e.type === "gol").length;
  const gc = state.events.filter((e) => e.type === "gol_contra").length;

  const toggleClock = () => setState((s) => {
    const c = s.clock;
    return { ...s, clock: c.running
      ? { ...c, running: false, accSec: Math.floor(c.accSec + (Date.now() - c.lastStart) / 1000) }
      : { ...c, running: true, lastStart: Date.now() } };
  });

  const addEvent = (type, extra = {}) => setState((s) => ({
    ...s, events: [...s.events, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, sec: elapsedOf(s.clock), min: Math.floor(elapsedOf(s.clock) / 60) + 1, type, ...extra }],
  }));

  const substitute = (outId, inId) => {
    setState((s) => {
      const sec = elapsedOf(s.clock);
      const min = Math.floor(sec / 60) + 1;
      const segments = { ...s.segments };
      segments[outId] = (segments[outId] || []).map((seg) => (seg.out == null ? { ...seg, out: sec } : seg));
      segments[inId] = [...(segments[inId] || []), { in: sec, out: null }];
      return {
        ...s, segments,
        gkId: outId === s.gkId ? inId : s.gkId,
        onField: s.onField.map((f) => (f.player_id === outId ? { ...f, player_id: inId } : f)),
        bench: [...s.bench.filter((x) => x !== inId), outId],
        events: [...s.events, { id: `${Date.now()}-sub`, sec, min, type: "cambio", out: outId, in: inId }],
      };
    });
    setSelectedBench(null);
    toast.success(`Cambio min ${minute}: sale #${byId[outId]?.jersey_number || "?"}, entra #${byId[inId]?.jersey_number || "?"}`);
  };

  const enterAlone = (inId, x, y) => {
    setState((s) => {
      const sec = elapsedOf(s.clock);
      const segments = { ...s.segments };
      segments[inId] = [...(segments[inId] || []), { in: sec, out: null }];
      return { ...s, segments,
        onField: [...s.onField, { player_id: inId, x, y }],
        bench: s.bench.filter((b) => b !== inId),
        events: [...s.events, { id: `${Date.now()}-in`, sec, min: Math.floor(sec / 60) + 1, type: "entra", player_id: inId }] };
    });
    setSelectedBench(null);
  };

  const exitAlone = (outId) => {
    setState((s) => {
      const sec = elapsedOf(s.clock);
      const segments = { ...s.segments };
      segments[outId] = (segments[outId] || []).map((seg) => (seg.out == null ? { ...seg, out: sec } : seg));
      return { ...s, segments,
        onField: s.onField.filter((f) => f.player_id !== outId),
        bench: [...s.bench, outId],
        events: [...s.events, { id: `${Date.now()}-out`, sec, min: Math.floor(sec / 60) + 1, type: "sale", player_id: outId }] };
    });
    setActionFor(null);
  };

  const sendOff = (pid) => {
    setState((s) => {
      const sec = elapsedOf(s.clock);
      const segments = { ...s.segments };
      segments[pid] = (segments[pid] || []).map((seg) => (seg.out == null ? { ...seg, out: sec } : seg));
      return { ...s, segments,
        onField: s.onField.filter((f) => f.player_id !== pid),
        expelled: [...(s.expelled || []), pid],
        events: [...s.events, { id: `${Date.now()}-roja`, sec, min: Math.floor(sec / 60) + 1, type: "roja", player_id: pid }] };
    });
    setActionFor(null);
    toast.error(`🟥 #${byId[pid]?.jersey_number || "?"} ${byId[pid]?.name?.split(" ")[0] || ""} expulsado — el equipo sigue con uno menos`);
  };

  // Drag de fichas del campo (reposicionar) — tap corto abre acciones / recibe cambio
  const onTokenDown = (e, pid) => {
    e.preventDefault();
    const rect = fieldRef.current.getBoundingClientRect();
    drag.current = { pid, startX: e.clientX, startY: e.clientY, moved: false, rect };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onTokenMove = (e) => {
    if (!drag.current) return;
    const { pid, startX, startY, rect } = drag.current;
    if (Math.abs(e.clientX - startX) + Math.abs(e.clientY - startY) > 10) drag.current.moved = true;
    if (!drag.current.moved) return;
    const x = Math.min(97, Math.max(3, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(97, Math.max(3, ((e.clientY - rect.top) / rect.height) * 100));
    setState((s) => ({ ...s, onField: s.onField.map((f) => (f.player_id === pid ? { ...f, x, y } : f)) }));
  };
  const onTokenUp = (e) => {
    if (!drag.current) return;
    const { pid, moved } = drag.current;
    drag.current = null;
    if (moved) return;
    if (selectedBench) { substitute(pid, selectedBench); return; }
    setActionFor(pid);
  };

  const onFieldTap = (e) => {
    if (!selectedBench || !fieldRef.current) return;
    if (state.onField.length >= MAX_FIELD[state.modality]) { toast.error("Campo completo — toca a un jugador para hacer el cambio"); return; }
    const rect = fieldRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    enterAlone(selectedBench, x, y);
  };

  const deleteEvent = (id) => setState((s) => {
    const ev = s.events.find((e) => e.id === id);
    let extra = {};
    if (ev?.type === "roja" && (s.expelled || []).includes(ev.player_id)) {
      extra = { expelled: (s.expelled || []).filter((p) => p !== ev.player_id), bench: [...s.bench, ev.player_id] };
    }
    return { ...s, ...extra, events: s.events.filter((e) => e.id !== id) };
  });

  const EVENT_LABEL = { gol: "⚽ Gol", gol_contra: "🥅 Gol en contra", asistencia: "🅰 Asistencia", amarilla: "🟨 Amarilla", roja: "🟥 Roja (expulsión)", cambio: "🔁 Cambio", entra: "▶ Entra", sale: "⏹ Sale", fuera_juego: "🚩 Fuera de juego", remate: "🎯 Disparo a puerta", falta_cometida: "⚠️ Falta cometida", falta_recibida: "🙌 Falta recibida", parada: "🧤 Parada", tiro_bloqueado: "🧱 Tiro bloqueado", duelo_aereo_ganado: "🦅 Duelo aéreo ganado", duelo_aereo_perdido: "💨 Duelo aéreo perdido" };

  const evtCount = (type) => state.events.filter((e) => e.type === type).length;

  // Insignias por jugador: goles, amarilla y flecha verde si entró de cambio.
  const goalsBy = {}; const yellowBy = {};
  state.events.forEach((e) => {
    if (e.type === "gol" && e.player_id) goalsBy[e.player_id] = (goalsBy[e.player_id] || 0) + 1;
    if (e.type === "amarilla" && e.player_id) yellowBy[e.player_id] = (yellowBy[e.player_id] || 0) + 1;
  });
  const tokenBadges = (pid) => {
    const b = [];
    if (goalsBy[pid]) b.push(`⚽${goalsBy[pid] > 1 ? "×" + goalsBy[pid] : ""}`);
    if (yellowBy[pid]) b.push("🟨");
    if (!state.starters.includes(pid)) b.push(<span className="text-emerald-400 font-black">▲</span>);
    return b;
  };
  const PLAYER_SUMS = { xuts_favor: evtCount("remate"), faltas_cometidas: evtCount("falta_cometida"), faltas_recibidas: evtCount("falta_recibida"), parades_porter: evtCount("parada"), tiros_bloqueados: evtCount("tiro_bloqueado"), duelos_aereos_ganados: evtCount("duelo_aereo_ganado"), duelos_aereos_perdidos: evtCount("duelo_aereo_perdido") };

  const COUNTERS = [
    ["xuts_favor", "Remates a favor"], ["xuts_contra", "Remates en contra"],
    ["corners_favor", "Corners a favor"], ["corners_contra", "Corners en contra"],
    ["faltas_cometidas", "Faltas cometidas"], ["faltas_recibidas", "Faltas recibidas"],
    ["parades_porter", "Paradas del portero"], ["tiros_bloqueados", "Tiros bloqueados"],
    ["duelos_aereos_ganados", "Duelos aéreos ganados"], ["duelos_aereos_perdidos", "Duelos aéreos perdidos"],
  ];
  const tc = state.teamCounters || {};

  return (
    <div className="max-w-3xl mx-auto pb-28">
      <div className="border-2 border-primary p-3 text-center space-y-2 mb-3">
        <div className="overline text-[10px]">{state.opponent} · {state.homeAway === "casa" ? "En casa" : "Fuera"} · {state.formation} · <span className={state.onField.length < MAX_FIELD[state.modality] ? "text-amber-400 font-bold" : "text-emerald-400"}>{state.onField.length}/{MAX_FIELD[state.modality]} en campo</span></div>
        <div className="flex items-center justify-center gap-4">
          <ScoreBtn label="Gol nuestro" onClick={() => setActionFor("__goal__")} testid="pz-goal-us" color="text-emerald-400 border-emerald-500" />
          <div className="font-heading font-black text-4xl tabular-nums" data-testid="pz-score">{gf}-{gc}</div>
          <ScoreBtn label="Gol rival" onClick={() => addEvent("gol_contra")} testid="pz-goal-them" color="text-destructive border-destructive" />
        </div>
        <div className="font-heading font-black text-3xl tabular-nums" data-testid="pz-clock">{fmt(elapsed)}</div>
        <div className="flex justify-center gap-2">
          <button data-testid="pz-clock-toggle" onClick={toggleClock} className={`h-10 px-5 inline-flex items-center gap-2 font-heading font-black uppercase text-xs tracking-wider ${state.clock.running ? "border-2 border-amber-400 text-amber-400" : "btn-volt"}`}>
            {state.clock.running ? <><Pause size={13} /> Pausa</> : <><Play size={13} /> {elapsed > 0 ? "Reanudar" : "Iniciar"}</>}
          </button>
          <button data-testid="pz-finish" onClick={onFinish} className="h-10 px-4 border-2 border-emerald-500 text-emerald-400 inline-flex items-center gap-1.5 font-heading font-black uppercase text-xs tracking-wider"><Flag size={12} /> Final</button>
        </div>
        {selectedBench && <div className="text-[11px] text-sky-400 font-mono">Entra #{byId[selectedBench]?.jersey_number || "?"} {byId[selectedBench]?.name} — toca al jugador que SALE (o un hueco libre)</div>}
        <div className="grid grid-cols-2 gap-1 pt-1">
          <button data-testid="pz-view-pizarra" onClick={() => setView("pizarra")} className={`h-10 border-2 font-heading font-black uppercase text-xs tracking-wider ${view === "pizarra" ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>🟢 Pizarra</button>
          <button data-testid="pz-view-datos" onClick={() => setView("datos")} className={`h-10 border-2 font-heading font-black uppercase text-xs tracking-wider ${view === "datos" ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>📊 Datos</button>
        </div>
        <button
          data-testid="pz-share-btn"
          onClick={async () => {
            setSharing(true);
            try {
              const res = await sharePizarra({ state, byId, teamName, goalsBy, yellowBy });
              if (res === "downloaded") toast.success("Imagen descargada — compártela por WhatsApp");
              else if (res === "shared") toast.success("Alineación compartida");
            } catch { toast.error("No se pudo generar la imagen"); }
            setSharing(false);
          }}
          disabled={sharing}
          className="w-full h-10 border-2 border-sky-500/50 text-sky-400 font-heading font-black uppercase text-xs tracking-wider hover:bg-sky-500/10 disabled:opacity-50"
        >
          {sharing ? "Generando imagen…" : "📤 Compartir alineación (imagen)"}
        </button>
        <button data-testid="pz-help-btn" onClick={() => setShowHelp(true)} className="text-[10px] text-muted-foreground underline font-mono">¿Cómo funciona la pizarra?</button>
      </div>

      {view === "datos" && (
        <div className="border border-border p-3 space-y-2" data-testid="pz-datos">
          <div className="overline mb-1">Acciones de jugador · toca a uno para registrar gol, tarjeta, falta…</div>
          <div className="flex gap-2 flex-wrap pb-2 border-b border-border/50" data-testid="pz-datos-players">
            {state.onField.map((f) => {
              const p = byId[f.player_id];
              return (
                <button key={f.player_id} data-testid={`pz-datos-player-${f.player_id}`} onClick={() => setActionFor(f.player_id)}>
                  <PlayerToken p={p} size={48} color={f.player_id === state.gkId ? "#059669" : "#1d4ed8"} badges={tokenBadges(f.player_id)} />
                </button>
              );
            })}
          </div>
          <div className="overline mb-1">Estadísticas del equipo · toca + o − durante el partido</div>
          {COUNTERS.map(([key, label]) => (
            <div key={key} className="flex items-center gap-2 border border-border/60 p-2">
              <span className="flex-1 font-heading font-bold text-xs uppercase">{label}
                {PLAYER_SUMS[key] > 0 && <span className="block text-[9px] font-mono text-sky-400 normal-case">+{PLAYER_SUMS[key]} registrado{PLAYER_SUMS[key] > 1 ? "s" : ""} por jugador en la pizarra</span>}
              </span>
              <button data-testid={`pz-cnt-minus-${key}`} onClick={() => setCounter(key, -1)} className="w-11 h-11 border-2 border-border text-xl font-black hover:border-destructive hover:text-destructive">−</button>
              <span data-testid={`pz-cnt-val-${key}`} className="w-10 text-center font-heading font-black text-2xl tabular-nums">{tc[key] || 0}</span>
              <button data-testid={`pz-cnt-plus-${key}`} onClick={() => setCounter(key, 1)} className="w-11 h-11 border-2 border-primary text-primary text-xl font-black hover:bg-primary/10">+</button>
            </div>
          ))}
          <p className="text-[10px] text-muted-foreground font-mono">Todo lo que registres aquí o en la pizarra se guarda junto al finalizar — las dos vistas son equivalentes.</p>
        </div>
      )}

      <div style={{ display: view === "pizarra" ? undefined : "none" }}>
      <div ref={fieldRef} onClick={onFieldTap} data-testid="pz-field"
        className="relative w-full select-none touch-none overflow-hidden"
        style={{ aspectRatio: "3/4", backgroundColor: "#0f6127", backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0.12) 100%), repeating-linear-gradient(180deg, rgba(255,255,255,0.04) 0 12.5%, rgba(0,0,0,0.06) 12.5% 25%)", border: "2px solid rgba(255,255,255,0.5)" }}>
        {/* líneas del campo */}
        <div className="absolute left-0 right-0 top-1/2 h-[2px] bg-white/50" />
        <div className="absolute left-1/2 top-1/2 w-[26%] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/50" />
        <div className="absolute left-1/2 bottom-0 w-[56%] h-[14%] -translate-x-1/2 border-2 border-b-0 border-white/50" />
        <div className="absolute left-1/2 bottom-0 w-[28%] h-[6%] -translate-x-1/2 border-2 border-b-0 border-white/50" />
        <div className="absolute left-1/2 top-0 w-[56%] h-[14%] -translate-x-1/2 border-2 border-t-0 border-white/50" />
        <div className="absolute left-1/2 top-0 w-[28%] h-[6%] -translate-x-1/2 border-2 border-t-0 border-white/50" />
        {state.onField.map((f) => {
          const p = byId[f.player_id];
          const isGK = f.player_id === state.gkId;
          return (
            <div key={f.player_id} data-testid={`pz-token-${f.player_id}`}
              onPointerDown={(e) => onTokenDown(e, f.player_id)} onPointerMove={onTokenMove} onPointerUp={onTokenUp}
              className="absolute -translate-x-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing"
              style={{ left: `${f.x}%`, top: `${f.y}%`, touchAction: "none" }}>
              <PlayerToken p={p} size={52} color={isGK ? "#059669" : "#1d4ed8"} selected={!!selectedBench} badges={tokenBadges(f.player_id)} />
            </div>
          );
        })}
      </div>

      {/* Banquillo */}
      <div className="border border-border border-t-0 p-3">
        <div className="overline mb-2">Reservas · toca uno y luego al jugador que sale</div>
        <div className="flex gap-2 flex-wrap" data-testid="pz-bench">
          {state.bench.map((pid) => {
            const p = byId[pid];
            const sel = selectedBench === pid;
            return (
              <button key={pid} data-testid={`pz-bench-${pid}`} onClick={() => setSelectedBench(sel ? null : pid)}
                className={`${sel ? "scale-110" : ""}`}>
                <PlayerToken p={p} size={46} color="#dc2626" selected={sel} badges={yellowBy[pid] ? ["🟨"] : []} />
              </button>
            );
          })}
          {state.bench.length === 0 && <span className="text-xs text-muted-foreground">Sin reservas.</span>}
        </div>
      </div>
      </div>

      {/* Registro de eventos */}
      {state.events.length > 0 && (
        <div className="border border-border border-t-0 p-3 max-h-44 overflow-y-auto" data-testid="pz-events">
          {[...state.events].reverse().map((ev) => {
            const P = (pid) => (byId[pid] ? `#${byId[pid].jersey_number || "?"} ${byId[pid].name.split(" ")[0]}` : "");
            const icon = (EVENT_LABEL[ev.type] || "•").split(" ")[0];
            const label = (EVENT_LABEL[ev.type] || ev.type).replace(/^\S+\s*/, "");
            return (
              <div key={ev.id} className="flex items-center gap-2.5 py-2 border-b border-border/30">
                <span className="w-9 h-6 flex items-center justify-center bg-primary/15 text-primary font-black text-[11px] font-mono flex-shrink-0">{ev.min}'</span>
                <span className="text-lg leading-none flex-shrink-0 w-6 text-center">{icon}</span>
                {ev.type === "cambio" ? (
                  <span className="flex-1 text-[11px] font-heading font-bold uppercase leading-tight">
                    <span className="text-red-400">Sale {P(ev.out)}</span> <span className="text-muted-foreground">→</span> <span className="text-emerald-400">Entra {P(ev.in)}</span>
                  </span>
                ) : (
                  <span className="flex-1 text-[11px] font-heading font-bold uppercase leading-tight">
                    <span className={ev.type === "gol" ? "text-emerald-400" : ev.type === "gol_contra" || ev.type === "roja" ? "text-red-400" : ev.type === "amarilla" ? "text-amber-400" : ""}>{label}</span>
                    {ev.player_id && byId[ev.player_id] && <span className="text-foreground"> — {P(ev.player_id)}</span>}
                  </span>
                )}
                <button data-testid={`pz-del-${ev.id}`} onClick={() => deleteEvent(ev.id)} className="text-destructive p-1.5"><X size={14} /></button>
              </div>
            );
          })}
        </div>
      )}

      <button data-testid="pz-abort" onClick={() => { if (window.confirm("¿Descartar el partido sin guardar?")) setState(null); }}
        className="mt-3 w-full h-9 border border-destructive/50 text-destructive text-[10px] font-heading uppercase tracking-widest inline-flex items-center justify-center gap-2">
        <RotateCcw size={12} /> Descartar partido
      </button>

      {/* Ayuda de la pizarra */}
      {showHelp && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-card border-2 border-primary w-full max-w-sm p-5 space-y-3" data-testid="pz-help">
            <div className="font-heading font-black text-xl uppercase tracking-tight">Cómo funciona la pizarra</div>
            <div className="space-y-2.5 text-sm">
              <div className="flex gap-2"><span className="text-primary font-black">1.</span><span><b>Hacer un cambio:</b> toca al <span className="text-red-400 font-bold">reserva</span> del banquillo y, justo después, toca al <span className="text-blue-400 font-bold">jugador del campo</span> que sale. El cambio queda registrado en el minuto exacto. ¡Sin límite: pueden entrar y salir las veces que quieras!</span></div>
              <div className="flex gap-2"><span className="text-primary font-black">2.</span><span><b>Recolocar:</b> arrastra cualquier muñeco del campo a la posición que quieras (no cuenta como cambio).</span></div>
              <div className="flex gap-2"><span className="text-primary font-black">3.</span><span><b>Goles, tarjetas, faltas, duelos…:</b> toca a un jugador del campo (sin reserva seleccionado) y elige la acción. La <b>🟥 roja</b> expulsa al jugador: sale del campo sin sustituto y el equipo sigue con uno menos.</span></div>
              <div className="flex gap-2"><span className="text-primary font-black">4.</span><span><b>Botón 📊 Datos:</b> lo mismo que la pizarra en formato lista — los jugadores arriba (toca para registrar acciones) y los contadores del equipo debajo. Todo se guarda junto al pulsar Final.</span></div>
            </div>
            <button data-testid="pz-help-close" onClick={closeHelp} className="btn-volt w-full h-11 justify-center">¡Entendido, a jugar!</button>
          </div>
        </div>
      )}

      {/* Menú de acciones sobre un jugador (o selector de goleador) */}
      {actionFor && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setActionFor(null)}>
          <div className="bg-card border border-border w-full max-w-sm p-4 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="pz-action-menu">
            {actionFor === "__goal__" ? (
              <>
                <div className="overline mb-2">⚽ ¿Quién marcó? (min {minute})</div>
                {state.onField.map((f) => (
                  <button key={f.player_id} data-testid={`pz-scorer-${f.player_id}`} onClick={() => { addEvent("gol", { player_id: f.player_id }); setActionFor(null); }} className="w-full h-12 border border-border hover:border-primary text-left px-2 font-heading font-bold text-xs uppercase flex items-center gap-2.5">
                    <MiniAvatar p={byId[f.player_id]} />
                    <span className="flex-1 truncate">{byId[f.player_id]?.name}</span>
                    <span className="text-muted-foreground">#{byId[f.player_id]?.jersey_number || "?"}</span>
                  </button>
                ))}
                <button data-testid="pz-scorer-skip" onClick={() => { addEvent("gol"); setActionFor(null); }} className="w-full h-11 border border-border text-muted-foreground text-xs font-heading uppercase">Sin especificar</button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-2 pb-2 border-b border-border/50">
                  <MiniAvatar p={byId[actionFor]} className="!w-11 !h-11 ring-2 ring-primary" />
                  <div className="min-w-0">
                    <div className="font-heading font-black text-sm uppercase truncate">#{byId[actionFor]?.jersey_number || "?"} {byId[actionFor]?.name}</div>
                    <div className="overline text-[9px]">Minuto {minute} · elige acción</div>
                  </div>
                </div>
                {[
                  ["gol", "⚽ Gol"], ["asistencia", "🅰 Asistencia"], ["remate", "🎯 Disparo a puerta"],
                  ...(actionFor === state.gkId ? [["parada", "🧤 Parada del portero"]] : []),
                  ["falta_cometida", "⚠️ Falta cometida"], ["falta_recibida", "🙌 Falta recibida"],
                  ["tiro_bloqueado", "🧱 Tiro bloqueado"], ["duelo_aereo_ganado", "🦅 Duelo aéreo ganado"], ["duelo_aereo_perdido", "💨 Duelo aéreo perdido"],
                  ["amarilla", "🟨 Tarjeta amarilla"], ["fuera_juego", "🚩 Fuera de juego"],
                ].map(([t, l]) => (
                  <button key={t} data-testid={`pz-act-${t}`} onClick={() => { addEvent(t, { player_id: actionFor }); setActionFor(null); }} className="w-full h-11 border border-border hover:border-primary text-left px-3 text-sm">{l}</button>
                ))}
                {state.onField.some((f) => f.player_id === actionFor) && (
                  <button data-testid="pz-act-roja" onClick={() => sendOff(actionFor)} className="w-full h-11 border-2 border-destructive text-destructive text-left px-3 text-sm font-bold">🟥 Tarjeta roja — expulsado (sale sin sustituto)</button>
                )}
                <button data-testid="pz-act-sale" onClick={() => exitAlone(actionFor)} className="w-full h-11 border border-sky-400/60 text-sky-400 text-left px-3 text-sm">⏹ Sale sin sustituto (lesión / atención médica)</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreBtn({ label, onClick, testid, color }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`h-9 px-3 border-2 text-[10px] font-heading font-black uppercase tracking-wider ${color}`}>
      +1 {label}
    </button>
  );
}

/* ---------------- SUMMARY / SAVE ---------------- */
function Summary({ state, setState, players, navigate }) {
  const [saving, setSaving] = useState(false);
  const byId = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);
  const gf = state.events.filter((e) => e.type === "gol").length;
  const gc = state.events.filter((e) => e.type === "gol_contra").length;

  const rows = useMemo(() => state.convocados.map((pid) => {
    const segs = state.segments[pid] || [];
    const secs = segs.reduce((a, s) => a + Math.max(0, (s.out ?? state.finalSec) - s.in), 0);
    const cnt = (t) => state.events.filter((e) => e.type === t && e.player_id === pid).length;
    return { pid, titular: state.starters.includes(pid), minutos: Math.round(secs / 60),
             gols: cnt("gol"), asist: cnt("asistencia"), tg: cnt("amarilla"), tr: cnt("roja"),
             shots: cnt("remate"), fc: cnt("falta_cometida"), fr: cnt("falta_recibida"), paradas: cnt("parada"),
             bloq: cnt("tiro_bloqueado"), dag: cnt("duelo_aereo_ganado"), dap: cnt("duelo_aereo_perdido") };
  }), [state]);

  const save = async (completeAfter = false) => {
    setSaving(true);
    try {
      const res = await api.post("/jornadas", {
        team_id: state.teamId, session_type: "partido", date: state.date,
        opponent: state.opponent, home_away: state.homeAway,
        team_stats: {
          ...(state.teamCounters || {}),
          gols_favor: gf, gols_contra: gc,
          xuts_favor: ((state.teamCounters || {}).xuts_favor || 0) + rows.reduce((a, r) => a + r.shots, 0),
          faltas_cometidas: ((state.teamCounters || {}).faltas_cometidas || 0) + rows.reduce((a, r) => a + r.fc, 0),
          faltas_recibidas: ((state.teamCounters || {}).faltas_recibidas || 0) + rows.reduce((a, r) => a + r.fr, 0),
          parades_porter: ((state.teamCounters || {}).parades_porter || 0) + rows.reduce((a, r) => a + r.paradas, 0),
          tiros_bloqueados: ((state.teamCounters || {}).tiros_bloqueados || 0) + rows.reduce((a, r) => a + r.bloq, 0),
          duelos_aereos_ganados: ((state.teamCounters || {}).duelos_aereos_ganados || 0) + rows.reduce((a, r) => a + r.dag, 0),
          duelos_aereos_perdidos: ((state.teamCounters || {}).duelos_aereos_perdidos || 0) + rows.reduce((a, r) => a + r.dap, 0),
          targetes_grogues: rows.reduce((a, r) => a + r.tg, 0),
          targetes_roges: rows.reduce((a, r) => a + r.tr, 0),
          resultado: gf > gc ? "V" : gf === gc ? "E" : "D",
        },
        player_stats: rows.map((r) => ({
          player_id: r.pid, convocado: true, titular: r.titular, minutos: r.minutos,
          gols: r.gols, asistencias: r.asist, targetes_grogues: r.tg, targetes_roges: r.tr,
          shots_on_goal: r.shots, faltas_cometidas: r.fc, faltas_recibidas: r.fr,
          ...(r.paradas > 0 || r.pid === state.gkId ? { parades: r.paradas } : {}),
          offsides_detail: state.events.filter((e) => e.type === "fuera_juego" && e.player_id === r.pid).map((e) => ({ minute: e.min })),
        })),
        substitutions: state.events.filter((e) => e.type === "cambio").map((e) => ({ minute: e.min, out_player_id: e.out, in_player_id: e.in })),
        notes: `Registrado con la Pizarra táctil (${state.modality} · ${state.formation})`,
        status: "final",
      });
      toast.success("Partido guardado en Jornadas");
      const newId = res.data?.id;
      setState(null);
      if (completeAfter && newId) navigate(`/coach/jornadas/new?edit=${newId}`);
      else navigate("/coach/jornadas");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const cambios = state.events.filter((e) => e.type === "cambio");
  return (
    <div className="max-w-2xl">
      <div className="overline">Pizarra · Resumen final</div>
      <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2 mb-1">{gf} - {gc}</h1>
      <p className="text-muted-foreground text-sm mb-4">vs {state.opponent} · {state.date} · {cambios.length} cambio{cambios.length === 1 ? "" : "s"} · minutos calculados automáticamente</p>
      <div className="border border-border divide-y divide-border/40 mb-4" data-testid="pz-summary">
        {rows.sort((a, b) => b.minutos - a.minutos).map((r) => (
          <div key={r.pid} className="p-2.5 flex items-center gap-3 text-sm">
            <MiniAvatar p={byId[r.pid]} />
            <span className="font-heading font-bold text-xs uppercase flex-1 truncate">#{byId[r.pid]?.jersey_number || "?"} {byId[r.pid]?.name} {r.titular && <span className="text-primary">· T</span>}</span>
            <span className="font-mono text-xs" data-testid={`pz-min-${r.pid}`}>{r.minutos}'</span>
            {r.gols > 0 && <span className="text-xs">⚽{r.gols}</span>}
            {r.asist > 0 && <span className="text-xs">🅰{r.asist}</span>}
            {r.shots > 0 && <span className="text-xs">🎯{r.shots}</span>}
            {r.paradas > 0 && <span className="text-xs">🧤{r.paradas}</span>}
            {r.fc > 0 && <span className="text-xs">⚠️{r.fc}</span>}
            {r.fr > 0 && <span className="text-xs">🙌{r.fr}</span>}
            {r.tg > 0 && <span className="text-xs">🟨{r.tg}</span>}
            {r.tr > 0 && <span className="text-xs">🟥{r.tr}</span>}
          </div>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button data-testid="pz-back-board" onClick={() => setState((s) => ({ ...s, phase: "board" }))} className="h-11 px-4 border border-border text-xs font-heading uppercase tracking-wider">Volver al partido</button>
        <button data-testid="pz-save" onClick={() => save(false)} disabled={saving} className="btn-volt flex-1 h-11 justify-center disabled:opacity-50"><Save size={15} /> {saving ? "Guardando…" : "Guardar jornada"}</button>
        <button data-testid="pz-save-complete" onClick={() => save(true)} disabled={saving} className="w-full sm:w-auto h-11 px-4 border-2 border-sky-400 text-sky-400 text-xs font-heading font-black uppercase tracking-wider hover:bg-sky-400/10 disabled:opacity-50">
          Guardar y completar estadísticas →
        </button>
      </div>
      <p className="text-[10px] text-muted-foreground font-mono mt-2">"Completar estadísticas" abre el partido en la pantalla clásica de jornadas para afinar cualquier dato con la plantilla completa del club.</p>
    </div>
  );
}
